import { Link } from "react-router-dom";
import { Tooltip } from "antd";
import { useEffect, useState } from "react";
import { ROLES } from "../constants/UserRoles";
import { Settings, ChevronLeft, ChevronRight } from "lucide-react";
import Icon from "../assets/Icon";
import getNavItems from "../constants/NavItems";
import SidebarCreditsCard from "./SidebarCreditsCard";

// ─── Nav config per role ──────────────────────────────────────────────────────

const DASHBOARD_PATH_BY_ROLE = {
  candidate: "/dashboard",
  [ROLES.global_admin]: "/global-admin/dashboard",
  [ROLES.org_admin]: "/org-admin/dashboard",
  [ROLES.b2b_candidate]: "/b2b-candidate/dashboard",
};

// ─── NavItem ──────────────────────────────────────────────────────────────────

function NavItem({ view, activePage, collapsed }) {
  const isActive = activePage === view.key;
  const IconComp = view.icon;

  return (
    <Tooltip
      color="white"
      overlayInnerStyle={{ color: "black", fontSize: "0.8rem" }}
      placement="right"
      title={collapsed ? view.display : false}
    >
      <Link
        to={view.path}
        className={`relative flex items-center gap-3 px-3 py-2.5 mb-0.5 rounded-lg no-underline
          transition-colors duration-150 group
          ${view.className ?? ""}
          ${
            isActive
              ? "bg-[#edc04326] text-[#edc043]"
              : "text-[#F2F8FE] hover:bg-white/[0.07]"
          }`}
      >
        {/* Active left-edge bar */}
        <span
          className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full
            bg-[#edc043] transition-all duration-200
            ${isActive ? "h-[60%]" : "h-0"}`}
        />

        {/* Icon */}
        <span className="flex-shrink-0 flex items-center">
          <IconComp
            size={18}
            strokeWidth={isActive ? 2.2 : 1.7}
            className="transition-transform duration-150 group-hover:scale-110"
          />
        </span>

        {/* Label — only dynamic part uses inline style */}
        <span
          className="text-sm overflow-hidden whitespace-nowrap transition-all duration-200"
          style={{
            opacity: collapsed ? 0 : 1,
            maxWidth: collapsed ? 0 : "160px",
            fontWeight: isActive ? 600 : 400,
          }}
        >
          {view.display}
        </span>
      </Link>
    </Tooltip>
  );
}

// ─── LowerItem ────────────────────────────────────────────────────────────────

function LowerItem({ view, activePage, collapsed }) {
  const isActive = activePage === view.key;
  const IconComp = view.icon;
  const isExternal = view.path.startsWith("http");

  const inner = (
    <div
      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer
        transition-colors duration-150
        ${
          isActive
            ? "bg-[#edc04320] text-[#edc043]"
            : "bg-white/[0.06] text-zinc-400 hover:bg-white/[0.1]"
        }`}
      onClick={isExternal ? () => window.open(view.path, "_blank") : undefined}
    >
      <span className="flex-shrink-0 flex items-center">
        <IconComp size={16} strokeWidth={1.7} />
      </span>
      <span
        className="text-[0.8rem] overflow-hidden whitespace-nowrap transition-all duration-200"
        style={{
          opacity: collapsed ? 0 : 1,
          maxWidth: collapsed ? 0 : "160px",
        }}
      >
        {view.display}
      </span>
    </div>
  );

  return (
    <Tooltip
      color="white"
      overlayInnerStyle={{ color: "black", fontSize: "0.8rem" }}
      placement="right"
      title={collapsed ? view.display : false}
    >
      {isExternal ? (
        <div>{inner}</div>
      ) : (
        <Link to={view.path} className="block no-underline">
          {inner}
        </Link>
      )}
    </Tooltip>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({
  diverSidebarClasses,
  userRole,
  activePage,
  collapseSidbar,
}) {
  console.log(userRole);
  const [collapsed, setCollapsed] = useState(collapseSidbar === "left");

  const navItems = getNavItems(userRole, diverSidebarClasses);
  const homePath = DASHBOARD_PATH_BY_ROLE[userRole] ?? "/dashboard";
  const lowerbar = [
    {
      display: "Account Settings",
      key: "settings",
      path: "/settings",
      icon: Settings,
    },
  ];

  useEffect(() => {
    if (window.innerWidth <= 1090) {
      setCollapsed(true);
      localStorage.setItem("toggleStatus", "left");
    } else {
      const isCollapsed = collapseSidbar === "left";
      setCollapsed(isCollapsed);
      localStorage.setItem("toggleStatus", isCollapsed ? "left" : "right");
    }
  }, []);

  const toggle = () => {
    setCollapsed((prev) => {
      localStorage.setItem("toggleStatus", prev ? "right" : "left");
      return !prev;
    });
  };

  return (
    <div
      // data-collapsed={collapse === "left" ? false : true}
      className="hidden md:flex relative flex-col min-h-screen bg-[#0f1623] px-3 py-5 flex-shrink-0 transition-all duration-[250ms]"
      style={{ width: collapsed ? "64px" : "220px" }}
    >
      {/* Logo */}
      <Link
        to={homePath}
        className="flex items-center gap-2.5 mb-8 no-underline overflow-hidden whitespace-nowrap"
      >
        <Icon type="jobdrona-logo" width={30} height={32} />
        <span
          className="font-bold text-[1.1rem] text-[#edc043] overflow-hidden whitespace-nowrap transition-all duration-200"
          style={{
            opacity: collapsed ? 0 : 1,
            width: collapsed ? 0 : "120px",
          }}
        >
          JobDrona
        </span>
      </Link>

      {/* Main nav */}
      <nav className="flex-1">
        {navItems.map((view, i) => (
          <NavItem
            key={i}
            view={view}
            activePage={activePage}
            collapsed={collapsed}
          />
        ))}
      </nav>
      {userRole !== "JOBDRONA_B2B_CANDIDATE" && (
        <SidebarCreditsCard collapsed={collapsed} />
      )}

      {/* Lower bar */}
      {/* <div className="border-t border-white/[0.07] pt-3">
        {lowerbar.map((view, i) => (
          <LowerItem
            key={i}
            view={view}
            activePage={activePage}
            collapsed={collapsed}
          />
        ))}
      </div> */}

      {/* Collapse toggle */}
      <button
        onClick={toggle}
        className="absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-6 rounded-full
          bg-[#1e2a3a] border border-white/[0.12] flex items-center justify-center
          cursor-pointer text-zinc-400 z-10 hover:bg-[#2a3a50] transition-colors duration-150"
      >
        {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
      </button>
    </div>
  );
}

export default Sidebar;
